import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Save, RotateCcw, FileText, TrendingUp,
  Search, CheckSquare, AlertCircle
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/CustomerInvoice.css';

/* ══════════════════════════════════════════════════════════════
   CustomerInvoiceForm
   - Invoice data comes ONLY from consignmentpaymentdetails
   - Shows all pending (uninvoiced) payments upfront
   - User selects rows → totals auto-calculate
   - No manual line item entry
════════════════════════════════════════════════════════════════ */
const CustomerInvoiceForm = ({ editData = null, onClose, onSaved }) => {
  const [loading,     setLoading]     = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  /* all pending payments from consignmentpaymentdetails */
  const [pendingRows, setPendingRows] = useState([]);

  /* selected ConsignmentPaymentID[] */
  const [selected,    setSelected]    = useState([]);

  /* search / filter */
  const [searchTerm,      setSearchTerm]      = useState('');
  const [filterCustomer,  setFilterCustomer]  = useState('');

  /* currencies */
  const [currencies, setCurrencies] = useState([]);

  /* invoice header */
  const [form, setForm] = useState({
    InvoiceNo:       '',
    InvoiceDate:     new Date().toISOString().slice(0, 10),
    DueDate:         '',
    CustomerID:      '',
    CurrencyID:      '',
    ExchangeRate:    1,
    PaymentTerms:    'net30',
    ReferenceNo:     '',
    Notes:           '',
    TermsConditions: '',
    TaxPercent:      0,
    DiscountPercent: 0,
    Status:          'Draft',
  });

  const paymentTermOptions = [
    { id: 'immediate', label: 'Immediate'   },
    { id: 'net15',     label: 'Net 15 Days' },
    { id: 'net30',     label: 'Net 30 Days' },
    { id: 'net45',     label: 'Net 45 Days' },
    { id: 'net60',     label: 'Net 60 Days' },
  ];

  /* ── ESC key ── */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  /* ── load on mount ── */
  useEffect(() => { loadInitialData(); }, [editData]);

  const loadInitialData = async () => {
    setLoadingData(true);
    setError('');
    try {
      const [pendingRes, currRes, invNoRes] = await Promise.all([
        apiClient.get('/CustomerInvoices.php?uninvoiced-payments=1'),
        apiClient.get('/Currencies.php'),
        editData ? Promise.resolve(null) : apiClient.get('/CustomerInvoices.php/next-number'),
      ]);

      if (pendingRes.data.success) setPendingRows(pendingRes.data.data || []);
      if (currRes.data.success)    setCurrencies(currRes.data.data    || []);

      if (editData) {
        await loadForEdit(editData, pendingRes.data.data || []);
      } else if (invNoRes?.data?.success) {
        setForm(p => ({ ...p, InvoiceNo: invNoRes.data.data.invoiceNo }));
      }
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingData(false);
    }
  };

  const loadForEdit = async (inv, allPending) => {
    let full = inv;
    if (!inv.Items) {
      try {
        const res = await apiClient.get(`/CustomerInvoices.php?id=${inv.InvoiceID}`);
        if (res.data.success) full = res.data.data;
      } catch (err) { console.error(err); }
    }
    setForm({
      InvoiceNo:       full.InvoiceNo       || '',
      InvoiceDate:     (full.InvoiceDate    || '').slice(0, 10),
      DueDate:         (full.DueDate        || '').slice(0, 10),
      CustomerID:      full.CustomerID      || '',
      CurrencyID:      full.CurrencyID      || '',
      ExchangeRate:    full.ExchangeRate    || 1,
      PaymentTerms:    full.PaymentTerms    || 'net30',
      ReferenceNo:     full.ReferenceNo     || '',
      Notes:           full.Notes           || '',
      TermsConditions: full.TermsConditions || '',
      TaxPercent:      full.TaxPercent      || 0,
      DiscountPercent: full.DiscountPercent || 0,
      Status:          full.Status          || 'Draft',
    });
    if (full.Items?.length) {
      setSelected(full.Items.map(i => i.ConsignmentPaymentID).filter(Boolean));
    }
  };

  /* ── derived: unique customers from pending rows ── */
  const customerOptions = useMemo(() => {
    const map = {};
    pendingRows.forEach(r => {
      if (r.CustomerID && r.customername) map[r.CustomerID] = r.customername;
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [pendingRows]);

  /* ── filtered visible rows ── */
  const visibleRows = useMemo(() => {
    return pendingRows.filter(r => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        !s ||
        (r.ConsignmentNo || '').toLowerCase().includes(s) ||
        (r.customername  || '').toLowerCase().includes(s) ||
        (r.itemname      || '').toLowerCase().includes(s) ||
        (r.PaymentMode   || '').toLowerCase().includes(s);
      const matchCust = !filterCustomer || String(r.CustomerID) === String(filterCustomer);
      return matchSearch && matchCust;
    });
  }, [pendingRows, searchTerm, filterCustomer]);

  /* ── selected row objects ── */
  const selectedRows = useMemo(
    () => pendingRows.filter(r => selected.includes(r.ConsignmentPaymentID)),
    [pendingRows, selected]
  );

  /* ── live totals ── */
  const totals = useMemo(() => {
    const sub  = selectedRows.reduce((s, r) => s + parseFloat(r.TotalAmt || 0), 0);
    const disc = sub  * (parseFloat(form.DiscountPercent || 0) / 100);
    const aft  = sub  - disc;
    const tax  = aft  * (parseFloat(form.TaxPercent      || 0) / 100);
    return { subTotal: sub, discountAmount: disc, taxAmount: tax, totalAmount: aft + tax };
  }, [selectedRows, form.DiscountPercent, form.TaxPercent]);

  /* ── auto-fill header from first selected row ── */
  useEffect(() => {
    if (selected.length === 1 && !editData) {
      const first = pendingRows.find(r => r.ConsignmentPaymentID === selected[0]);
      if (first) {
        setForm(p => ({
          ...p,
          CustomerID:  first.CustomerID          || p.CustomerID,
          CurrencyID:  first.RevenueCurrencyID   || p.CurrencyID,
          ExchangeRate: parseFloat(first.RevenueExchangeRate || 1),
        }));
        if (first.CustomerID) setFilterCustomer(String(first.CustomerID));
      }
    }
  }, [selected]);

  /* ── selection helpers ── */
  const toggleRow = (id) =>
    setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const allVisibleSelected =
    visibleRows.length > 0 &&
    visibleRows.every(r => selected.includes(r.ConsignmentPaymentID));

  const toggleAll = () => {
    const ids = visibleRows.map(r => r.ConsignmentPaymentID);
    if (allVisibleSelected) {
      setSelected(p => p.filter(id => !ids.includes(id)));
    } else {
      setSelected(p => [...new Set([...p, ...ids])]);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  /* ── save ── */
  const handleSave = async () => {
    if (!form.CustomerID)   { setError('Customer is required — select a row first'); return; }
    if (!selected.length)   { setError('Please select at least one payment row');    return; }

    const uniqueCustomers = [...new Set(selectedRows.map(r => String(r.CustomerID)))];
    if (uniqueCustomers.length > 1) {
      setError('All selected rows must belong to the same customer');
      return;
    }

    setLoading(true); setError(''); setSuccess('');
    try {
      const items = selectedRows.map((r, i) => ({
        LineNumber:           i + 1,
        ConsignmentID:        r.ConsignmentID,
        ConsignmentPaymentID: r.ConsignmentPaymentID,
        ItemID:               r.ItemID || null,
        Description:          buildDesc(r),
        Quantity:             1,
        UnitPrice:            parseFloat(r.TotalAmt || 0),
        TaxPercent:           0,
        DiscountPercent:      0,
        Amount:               parseFloat(r.TotalAmt || 0),
      }));

      const payload = {
        InvoiceNo:       form.InvoiceNo,
        InvoiceDate:     form.InvoiceDate,
        DueDate:         form.DueDate    || null,
        CustomerID:      form.CustomerID,
        CurrencyID:      form.CurrencyID || null,
        ExchangeRate:    parseFloat(form.ExchangeRate),
        PaymentTerms:    form.PaymentTerms,
        ReferenceNo:     form.ReferenceNo    || null,
        Notes:           form.Notes          || null,
        TermsConditions: form.TermsConditions || null,
        SubTotal:        totals.subTotal,
        TaxPercent:      parseFloat(form.TaxPercent),
        TaxAmount:       totals.taxAmount,
        DiscountPercent: parseFloat(form.DiscountPercent),
        DiscountAmount:  totals.discountAmount,
        TotalAmount:     totals.totalAmount,
        BalanceAmount:   totals.totalAmount,
        Status:          form.Status,
        Items:           items,
        ...(editData ? { InvoiceID: editData.InvoiceID } : {}),
      };

      const res = editData
        ? await apiClient.put('/CustomerInvoices.php', payload)
        : await apiClient.post('/CustomerInvoices.php', payload);

      if (res.data.success) {
        const no = res.data.data?.InvoiceNo || form.InvoiceNo;
        setSuccess(`Invoice ${no} ${editData ? 'updated' : 'created'} successfully!`);
        setTimeout(() => onSaved(), 900);
      } else {
        throw new Error(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildDesc = (r) => {
    let d = r.ConsignmentNo || '';
    if (r.itemname)    d += ` – ${r.itemname}`;
    if (r.PaymentMode) d += ` (${r.PaymentMode})`;
    return d;
  };

  const handleClear = () => {
    setSelected([]); setSearchTerm(''); setFilterCustomer('');
    setError(''); setSuccess('');
    if (!editData) setForm(p => ({ ...p, CustomerID: '', CurrencyID: '' }));
  };

  /* ────────────────────────── RENDER ─────────────────────────── */
  return (
    <div
      className="ci-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ci-modal" onClick={(e) => e.stopPropagation()}>

        {/* ═══ HEADER ═══ */}
        <div className="ci-modal-header">
          <div className="ci-modal-header-left">
            <div className="ci-modal-icon">
              <FileText size={26} color="#fff" />
            </div>
            <div className="ci-modal-header-row">
              <span className="ci-modal-title">
                {editData ? 'Edit Customer Invoice' : 'New Customer Invoice'}
              </span>
              <span className="ci-modal-badge">
                📋 {form.InvoiceNo || '—'}
              </span>
              {selected.length > 0 && (
                <span className="ci-modal-badge-green">
                  {selected.length} row{selected.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          </div>
          <button
            className="ci-modal-close"
            onClick={onClose}
            title="Close (ESC)"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
          >
            <X size={20} color="#fff" />
          </button>
        </div>

        {/* ═══ TOOLBAR ═══ */}
        <div className="ci-modal-toolbar">
          <button
            className="ci-tb-btn ci-tb-save"
            onClick={handleSave}
            disabled={loading || !selected.length || loadingData}
          >
            {loading
              ? <><span className="ci-btn-spinner" /> Saving…</>
              : <><Save size={16} /> {editData ? 'Update Invoice' : 'Save Invoice'}</>}
          </button>
          <button className="ci-tb-btn" onClick={handleClear} disabled={loading}>
            <RotateCcw size={16} /> Clear
          </button>
          {selected.length > 0 && (
            <span className="ci-toolbar-total">
              {selected.length} payment{selected.length !== 1 ? 's' : ''}
              &nbsp;→&nbsp;
              <strong>{totals.totalAmount.toFixed(2)}</strong>
            </span>
          )}
        </div>

        {/* ═══ ALERTS ═══ */}
        {error   && (
          <div className="ci-alert ci-alert-error">
            <AlertCircle size={14} style={{ flexShrink: 0 }} /> {error}
          </div>
        )}
        {success && (
          <div className="ci-alert ci-alert-success">✓ {success}</div>
        )}

        {/* ═══ BODY ═══ */}
        <div className="ci-modal-body">
          {loadingData ? (
            <div className="ci-loading" style={{ padding: 80 }}>
              <div className="ci-spinner" />
              <span>Loading pending payments…</span>
            </div>
          ) : (
            <>
              {/* ──── TOP: header form + summary panel ──── */}
              <div className="ci-form-top">

                {/* Left: Invoice header fields */}
                <div className="ci-section ci-section-grow">
                  <h3 className="ci-section-title"><FileText size={15} /> Invoice Details</h3>
                  <div className="ci-grid-2">

                    <div className="ci-field">
                      <label>Invoice No</label>
                      <input type="text" value={form.InvoiceNo} readOnly
                        className="ci-readonly" style={{ fontWeight: 700, background: '#e3f2fd' }} />
                    </div>

                    <div className="ci-field">
                      <label>Status</label>
                      <select name="Status" value={form.Status} onChange={handleFormChange}>
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Paid">Paid</option>
                        <option value="Partially Paid">Partially Paid</option>
                        <option value="Overdue">Overdue</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div className="ci-field">
                      <label>Invoice Date *</label>
                      <input type="date" name="InvoiceDate" value={form.InvoiceDate} onChange={handleFormChange} />
                    </div>

                    <div className="ci-field">
                      <label>Due Date</label>
                      <input type="date" name="DueDate" value={form.DueDate} onChange={handleFormChange} />
                    </div>

                    <div className="ci-field">
                      <label>Currency</label>
                      <select name="CurrencyID" value={form.CurrencyID} onChange={handleFormChange}>
                        <option value="">— Select —</option>
                        {currencies.map(c => (
                          <option key={c.currencyid} value={c.currencyid}>
                            {c.currencycode} – {c.currencyname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ci-field">
                      <label>Exchange Rate</label>
                      <input type="number" step="0.0001" name="ExchangeRate"
                        value={form.ExchangeRate} onChange={handleFormChange} />
                    </div>

                    <div className="ci-field">
                      <label>Payment Terms</label>
                      <select name="PaymentTerms" value={form.PaymentTerms} onChange={handleFormChange}>
                        {paymentTermOptions.map(pt => (
                          <option key={pt.id} value={pt.id}>{pt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="ci-field">
                      <label>Reference No</label>
                      <input type="text" name="ReferenceNo" value={form.ReferenceNo}
                        onChange={handleFormChange} placeholder="PO#, Job#…" />
                    </div>

                    <div className="ci-field ci-span-2">
                      <label>Notes</label>
                      <textarea name="Notes" value={form.Notes} onChange={handleFormChange}
                        rows={2} placeholder="Additional notes for the customer…" />
                    </div>

                    <div className="ci-field ci-span-2">
                      <label>Terms &amp; Conditions</label>
                      <textarea name="TermsConditions" value={form.TermsConditions}
                        onChange={handleFormChange} rows={2} placeholder="Payment terms, late fees…" />
                    </div>

                  </div>
                </div>

                {/* Right: Summary panel */}
                <div className="ci-section ci-summary-panel">
                  <h3 className="ci-section-title"><TrendingUp size={15} /> Invoice Summary</h3>

                  {selected.length === 0 ? (
                    <div className="ci-summary-empty">
                      <CheckSquare size={40} style={{ opacity: 0.15, marginBottom: 10 }} />
                      <p>Select consignment payments<br />from the list below</p>
                    </div>
                  ) : (
                    <>
                      <div className="ci-summary-customer">
                        <div className="ci-summary-label">Customer</div>
                        <div className="ci-summary-value">{selectedRows[0]?.customername || '—'}</div>
                      </div>

                      <div className="ci-summary-adjustments">
                        <div className="ci-field">
                          <label>Discount %</label>
                          <input type="number" step="0.01" name="DiscountPercent"
                            value={form.DiscountPercent} onChange={handleFormChange} placeholder="0.00" />
                        </div>
                        <div className="ci-field">
                          <label>Tax %</label>
                          <input type="number" step="0.01" name="TaxPercent"
                            value={form.TaxPercent} onChange={handleFormChange} placeholder="0.00" />
                        </div>
                      </div>

                      <div className="ci-totals-box" style={{ marginTop: 14 }}>
                        <div className="ci-total-row">
                          <span>Rows Selected</span>
                          <span className="ci-bold">{selected.length}</span>
                        </div>
                        <div className="ci-total-row">
                          <span>Sub Total</span>
                          <span className="ci-mono">{totals.subTotal.toFixed(2)}</span>
                        </div>
                        {parseFloat(form.DiscountPercent) > 0 && (
                          <div className="ci-total-row">
                            <span>Discount ({form.DiscountPercent}%)</span>
                            <span className="ci-mono">−{totals.discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {parseFloat(form.TaxPercent) > 0 && (
                          <div className="ci-total-row">
                            <span>Tax ({form.TaxPercent}%)</span>
                            <span className="ci-mono">+{totals.taxAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="ci-total-row ci-grand-total">
                          <span>Total Amount</span>
                          <span className="ci-mono">{totals.totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

              </div>{/* end ci-form-top */}

              {/* ──── PENDING PAYMENTS TABLE ──── */}
              <div className="ci-section">

                {/* section header with filters */}
                <div className="ci-pending-header">
                  <h3 className="ci-section-title" style={{ margin: 0, border: 0, paddingBottom: 0 }}>
                    <CheckSquare size={15} />
                    Pending Consignment Payments
                    <span className="ci-pending-count">{pendingRows.length} pending</span>
                  </h3>

                  <div className="ci-pending-filters">
                    {/* customer filter */}
                    <select
                      className="ci-filter-select"
                      value={filterCustomer}
                      onChange={(e) => setFilterCustomer(e.target.value)}
                    >
                      <option value="">All Customers</option>
                      {customerOptions.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>

                    {/* search */}
                    <div className="ci-search-wrap" style={{ minWidth: 240 }}>
                      <Search size={14} className="ci-search-icon" />
                      <input
                        className="ci-search-input"
                        type="text"
                        placeholder="Search consignment, item, mode…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button className="ci-search-clear" onClick={() => setSearchTerm('')}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  {pendingRows.length === 0 ? (
                    <div className="ci-empty" style={{ padding: '40px 0' }}>
                      <CheckSquare size={40} style={{ opacity: 0.15, marginBottom: 10 }} />
                      <h3>No Pending Payments</h3>
                      <p>All consignment payments have already been invoiced.</p>
                    </div>
                  ) : visibleRows.length === 0 ? (
                    <div className="ci-empty" style={{ padding: '24px 0' }}>
                      <p>No results match your search or filter.</p>
                    </div>
                  ) : (
                    <div className="ci-pending-table-wrap">
                      <table className="ci-table">
                        <thead>
                          <tr>
                            <th style={{ width: 40, textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={toggleAll}
                                title="Select / deselect all visible"
                              />
                            </th>
                            <th>Consignment No</th>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Item</th>
                            <th>Payment Mode</th>
                            <th>Currency</th>
                            <th className="ci-th-right">Amount</th>
                            <th className="ci-th-right">Tax Amt</th>
                            <th className="ci-th-right">Total</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleRows.map(row => {
                            const isSel = selected.includes(row.ConsignmentPaymentID);
                            return (
                              <tr
                                key={row.ConsignmentPaymentID}
                                className={`ci-tr ci-tr-selectable${isSel ? ' ci-tr-selected' : ''}`}
                                onClick={() => toggleRow(row.ConsignmentPaymentID)}
                              >
                                <td
                                  style={{ textAlign: 'center' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => toggleRow(row.ConsignmentPaymentID)}
                                  />
                                </td>
                                <td>
                                  <span className="ci-consignment-no">{row.ConsignmentNo}</span>
                                </td>
                                <td style={{ whiteSpace: 'nowrap', color: '#666', fontSize: 13 }}>
                                  {row.ConsignmentDate
                                    ? new Date(row.ConsignmentDate).toLocaleDateString()
                                    : '—'}
                                </td>
                                <td>
                                  <div className="ci-customer-name">{row.customername || '—'}</div>
                                </td>
                                <td style={{ fontSize: 13 }}>{row.itemname    || '—'}</td>
                                <td style={{ fontSize: 13 }}>{row.PaymentMode || '—'}</td>
                                <td style={{ fontSize: 13 }}>{row.RevenueCurrencyCode || '—'}</td>
                                <td className="ci-td-right ci-mono">
                                  {parseFloat(row.Amount  || 0).toFixed(2)}
                                </td>
                                <td className="ci-td-right ci-mono">
                                  {parseFloat(row.TaxAmt  || 0).toFixed(2)}
                                </td>
                                <td className="ci-td-right ci-mono ci-bold">
                                  {parseFloat(row.TotalAmt || 0).toFixed(2)}
                                </td>
                                <td style={{ fontSize: 12, color: '#888', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.Remarks || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="ci-tfoot-row">
                            <td colSpan={7} style={{ textAlign: 'right', fontWeight: 600, color: '#555', paddingRight: 12, fontSize: 13 }}>
                              Showing {visibleRows.length} rows — total:
                            </td>
                            <td className="ci-td-right ci-mono" style={{ fontWeight: 600 }}>
                              {visibleRows.reduce((s, r) => s + parseFloat(r.Amount  || 0), 0).toFixed(2)}
                            </td>
                            <td className="ci-td-right ci-mono" style={{ fontWeight: 600 }}>
                              {visibleRows.reduce((s, r) => s + parseFloat(r.TaxAmt  || 0), 0).toFixed(2)}
                            </td>
                            <td className="ci-td-right ci-mono" style={{ fontWeight: 700, color: 'var(--ci-purple-mid)' }}>
                              {visibleRows.reduce((s, r) => s + parseFloat(r.TotalAmt || 0), 0).toFixed(2)}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>

            </>
          )}
        </div>{/* end body */}

        {/* ═══ FOOTER ═══ */}
        <div className="ci-modal-footer">
          <div className="ci-footer-info">
            {selected.length > 0
              ? `${selected.length} payment${selected.length !== 1 ? 's' : ''} selected · Total: ${totals.totalAmount.toFixed(2)}`
              : 'No payments selected'}
          </div>
          <button className="ci-tb-btn" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="ci-tb-btn ci-tb-save"
            onClick={handleSave}
            disabled={loading || !selected.length || loadingData}
          >
            {loading
              ? <><span className="ci-btn-spinner" /> Saving…</>
              : <><Save size={16} /> {editData ? 'Update Invoice' : 'Save Invoice'}</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CustomerInvoiceForm;